import * as React from 'react';
import { ToolOutlined, DownOutlined, RightOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { Message, ToolCall } from '../../../actions/chatActions';
import { ToolCallStatus, RealTimeToolCall, ToolCallUpdate } from './types';

interface MessageItemProps {
  message: Message | ToolCall;
  messageIndex: number;
  classes: any;
  onToggleToolCall: (index: number) => void;
  toolCallUpdates?: Map<string, ToolCallUpdate>;
  currentLayerIndex?: number;
}

const MessageItem: React.FC<MessageItemProps> = ({ 
  message, 
  messageIndex, 
  classes, 
  onToggleToolCall,
  toolCallUpdates = new Map(),
  currentLayerIndex = 0
}) => {
  const getToolCallStatus = (toolCall: ToolCall): ToolCallStatus => {
    if (toolCall.status) {
      return toolCall.status as ToolCallStatus;
    }
    
    const updates = Array.from(toolCallUpdates.values());
    
    if ('id' in toolCall && toolCall.id) {
      const updateById = updates.find(update => update.id === toolCall.id);
      if (updateById) {
        return updateById.status;
      }
    }
    
    return ToolCallStatus.PENDING;
  };

  const getStatusIcon = (status: ToolCallStatus) => {
    switch (status) {
      case ToolCallStatus.PENDING:
        return <LoadingOutlined className={`${classes.toolCallStatusIcon} ${classes.toolCallStatusIconPending}`} />;
      case ToolCallStatus.RUNNING:
        return <LoadingOutlined className={`${classes.toolCallStatusIcon} ${classes.toolCallStatusIconRunning}`} spin />;
      case ToolCallStatus.COMPLETED:
        return <CheckCircleOutlined className={`${classes.toolCallStatusIcon} ${classes.toolCallStatusIconCompleted}`} />;
      case ToolCallStatus.FAILED:
        return <CloseCircleOutlined className={`${classes.toolCallStatusIcon} ${classes.toolCallStatusIconFailed}`} />;
      default:
        return <LoadingOutlined className={classes.toolCallStatusIcon} />;
    }
  };

  const getStatusText = (status: ToolCallStatus) => {
    switch (status) {
      case ToolCallStatus.PENDING:
        return 'Pending';
      case ToolCallStatus.RUNNING:
        return 'Running';
      case ToolCallStatus.COMPLETED:
        return 'Completed';
      case ToolCallStatus.FAILED:
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  const getStatusTextClass = (status: ToolCallStatus) => {
    switch (status) {
      case ToolCallStatus.PENDING:
        return classes.toolCallStatusPending;
      case ToolCallStatus.RUNNING:
        return classes.toolCallStatusRunning;
      case ToolCallStatus.COMPLETED:
        return classes.toolCallStatusCompleted;
      case ToolCallStatus.FAILED:
        return classes.toolCallStatusFailed;
      default:
        return '';
    }
  };

  const renderToolCall = (toolCall: ToolCall) => {
    const status = getToolCallStatus(toolCall);
    const statusIcon = getStatusIcon(status);
    const statusText = getStatusText(status);
    const statusTextClass = getStatusTextClass(status);
    
    const isCurrentLayer = toolCall.layerIndex === currentLayerIndex;
    const isActiveLayer = toolCall.layerIndex === currentLayerIndex && status === ToolCallStatus.RUNNING;
    
    return (
      <div className={`${classes.toolCallContainer} ${isCurrentLayer ? classes.currentLayerToolCall : ''}`}>
        <div 
          className={`${classes.toolCallHeader} ${isActiveLayer ? classes.activeLayerToolCall : ''}`}
          onClick={() => onToggleToolCall(messageIndex)}
        >
          <div className={classes.toolCallName}>
            <ToolOutlined className={classes.toolCallIcon} />
            {toolCall.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {statusIcon}
            <span className={`${classes.toolCallStatus} ${statusTextClass}`}>
              {statusText}
            </span>
            <span className={classes.toolCallTime}>
              {toolCall.timestamp.toLocaleTimeString()}
            </span>
            {toolCall.isExpanded ? (
              <DownOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />
            ) : (
              <RightOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />
            )}
          </div>
        </div>
        {toolCall.isExpanded && (
          <div className={classes.toolCallResponse}>
            {status === ToolCallStatus.FAILED && toolCall.error ? (
              <div className={classes.toolCallErrorText} style={{ marginBottom: '8px' }}>
                Error: {toolCall.error}
              </div>
            ) : null}
            
            {status === ToolCallStatus.COMPLETED && toolCall.content ? (
              <div>
                <div className={classes.toolCallResponseLabel}>
                  Response:
                </div>
                <pre className={classes.toolCallResponseContent}>
                  {typeof toolCall.content === 'string' 
                    ? toolCall.content 
                    : JSON.stringify(toolCall.content, null, 2)
                  }
                </pre>
              </div>
            ) : null}
            
            {status === ToolCallStatus.RUNNING ? (
              <div className={classes.toolCallProcessingText}>
                <LoadingOutlined spin style={{ marginRight: '8px' }} />
                Processing...
              </div>
            ) : null}
            
            {status === ToolCallStatus.PENDING ? (
              <div className={classes.toolCallPendingText}>
                Waiting to start...
                {toolCall.layerIndex !== undefined && toolCall.layerIndex > currentLayerIndex && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                    (Layer {toolCall.layerIndex + 1} - waiting for previous layers)
                  </span>
                )}
              </div>
            ) : null}
            
            {status === ToolCallStatus.FAILED && !toolCall.content ? (
              <div className={classes.toolCallErrorText}>
                Tool execution failed
              </div>
            ) : null}
            
            {status === ToolCallStatus.COMPLETED && toolCall.startTime && toolCall.endTime ? (
              <div className={classes.toolCallExecutionTime}>
                Execution time: {Math.round((toolCall.endTime.getTime() - toolCall.startTime.getTime()) / 1000)}s
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const renderMessage = (msg: Message) => {
    const messageClass = msg.sender === 'user' ? classes.userMessage : classes.aiMessage;
    const content = msg.sender === 'ai' && msg.formattedContent 
      ? msg.formattedContent 
      : msg.content;
    
    return (
      <div className={messageClass}>
        {content}
      </div>
    );
  };

  if (message.sender === 'toolCall') {
    return renderToolCall(message as ToolCall);
  }

  return (
    <div className={`${classes.messageItem} ${message.sender === 'user' ? classes.userMessageItem : ''}`}>
      <div className={`${classes.messageAvatar} ${message.sender === 'user' ? classes.userMessageAvatar : ''}`}>
        {message.sender === 'user' ? 'You' : 'AI'}
      </div>
      <div className={classes.messageContent}>
        {renderMessage(message as Message)}
        <div className={classes.messageTime}>
          {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
        </div>
      </div>
    </div>
  );
};

export default MessageItem; 